import '../ActivityActions.scss';
import React, { useEffect, useState } from 'react';
import { IFormValues } from '@models/Forms/IForms';
import { getABookFormValues } from '@utils/abookUtils';
import DueTimeModeTypeOption, { DueTimeModeType } from '@/types/DueTimeType';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import {
    IRequestAdditionalApprovalModel,
    IRequestAdditionalApprovalSend,
} from '@models/document/activityExecutors/IRequestAdditionalApprovalModel';
import { ActivityExecutionService } from '@services/actions/ActivityExecutionService';
import { Controller, useForm } from 'react-hook-form';
import { IOption } from '@/types';
import DurationSpanTypeOption from '@/types/DurationSpanType';
import Modal from '@atoms/Modal';
import clsx from 'clsx';
import Button from '@atoms/Button';
import { MdClose } from 'react-icons/md';
import Preloader from '@atoms/Preloader';
import Hint from '@atoms/Hint';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@controls/HookFormProvider';
import AbookpickerControl from '@controls/AbookpickerControl';
import FormControl from '@controls/FormControl';
import InputNumberControl from '@controls/InputNumberControl';
import SelectControl from '@controls/SelectControl';
import DatepickerControl from '@controls/DatepickerControl';
import TextareaControl from '@controls/TextareaControl';
import Select from '@atoms/Select';
import { IRequestAddApprovalProps } from './IRequestAddApprovalProps';
import { IRequestAddApprovalFormData } from './IRequestAddApprovalFormData';

export const RequestAddApprovalModal: React.FC<IRequestAddApprovalProps> = (props: IRequestAddApprovalProps) => {
    const [formValues, setFormValues] = useState<string>('{}');
    const activated = React.useRef(false);

    const getFormValues = async (fValues?: IFormValues) => {
        const vals = await getABookFormValues(props.docId, fValues);
        if (activated.current && vals) {
            setFormValues(vals);
        }
    };

    const [periodType, setPeriodType] = useState<DueTimeModeType>(DueTimeModeType.Indefinite);
    const [executors, setExecutors] = useState<IAddressBookData[]>();

    const [model, setModel] = useState<IRequestAdditionalApprovalModel>();
    const [errorText, setErrorText] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);

    const serv = new ActivityExecutionService();

    const onSubmit = (form: IRequestAddApprovalFormData) => {
        if (!form.taskText) {
            setErrorText('Не указан текст задачи');
            return;
        }
        if ((form?.executors?.length ?? 0) == 0) {
            setErrorText('Не указаны исполнители');
            return;
        }

        let data: IRequestAdditionalApprovalSend = {
            taskText: form.taskText,
            dueTimeMode: DueTimeModeType[periodType],
            executorsIds: executors?.map((x) => x.key) ?? [],
            periodDate: form.periodDate,
            termCount: form.termCount,
            termType: form?.termType ? form?.termType[0].value : undefined,
            activityKeys: [Number(props.activityId)],
            finishedNotificationText: model?.finishedNotificationText ?? '',
        };

        setLoading(true);
        serv.sendRequestAdditionalApproval(props.actionKey, props.activityId, data)
            .then((res) => {
                if (props.completeHandler) props.completeHandler(true);
            })
            .catch((err) => {
                setErrorText(err);
            })
            .finally(() => setLoading(false));
    };

    const formState = useForm<IRequestAddApprovalFormData>({
        mode: 'onBlur', // "onChange"
    });

    const onPeriodTypeChange = (option: IOption[]) => {
        if (option.length == 0) return;
        let type = DueTimeModeType[option[0].value as keyof typeof DueTimeModeType];
        setPeriodType(type);
    };

    useEffect(() => {
        setLoading(true);
        activated.current = true;

        serv.getRequestAdditionalApprovalModel(props.actionKey, props.activityId).then((res) => {
            setModel(res.data);
            if (res.data.defaultDueTime) {
                setPeriodType(DueTimeModeType[res.data.defaultDueTime.type]);
                formState.reset({
                    termType: DurationSpanTypeOption.filter((x) => x.value === res.data.defaultDueTime.timeSpanType),
                });
            }
            getFormValues(res.data?.formValues);
            setLoading(false);
        });
        return () => {
            activated.current = false;
        };
    }, []);

    return (
        <Modal
            className={clsx('modal-dlg-container')}
            size={props.modalSize}
            header={
                <div className={clsx('box')}>
                    <div>
                        <span>Запрос дополнительного согласования</span>
                    </div>
                    <div className={clsx('left')}>
                        <Button
                            buttonType="text"
                            textColor="neutral"
                            size="xs"
                            aria-label="Закрыть окно"
                            onClick={() => {
                                if (props?.completeHandler) props?.completeHandler(false);
                            }}
                            startAdornment={<MdClose size="24" />}
                        />
                    </div>
                </div>
            }
            footer={
                <div className="modal-dlg-buttons">
                    <Button
                        size="s"
                        aria-label={props.okButtonText}
                        onClick={(e) => {
                            let formData = formState.getValues() as IRequestAddApprovalFormData;
                            onSubmit(formData);
                        }}
                        disabled={loading}
                    >
                        {props.okButtonText}
                    </Button>
                    <Button
                        buttonType="light"
                        size="s"
                        aria-label={props.cancelButtonText}
                        onClick={() => {
                            if (props.completeHandler) props.completeHandler(false);
                        }}
                        disabled={loading}
                    >
                        {props.cancelButtonText}
                    </Button>
                </div>
            }
        >
            {loading ? (
                <Preloader size="m" position="static" />
            ) : (
                <div>
                    {errorText && <Hint title={`Ошибка: ${errorText}`} variant="red" />}

                    <FormProviderControl formMethods={formState} onSubmit={onSubmit}>
                        <HookFormProvider controller={Controller}>
                            <AbookpickerControl
                                label="Исполнители"
                                placeholder="Исполнители"
                                isMultiple={true}
                                title="Исполнители"
                                displayValue={executors?.map((x) => x.fullName)?.join(',') ?? ''}
                                formValues={formValues}
                                name="executors"
                                tabsSettings={model?.tabs}
                                externalSearch={model?.externalSearch}
                                required
                                formState={formState.formState}
                                showChips={false}
                                control={formState.control}
                                onSetValues={(value: IAddressBookData[]) => {
                                    setExecutors(value);
                                }}
                            />

                            <FormControl
                                name="periodType"
                                label="Срок исполнения"
                                formState={formState.formState}
                                required
                            >
                                <Select
                                    placeholder="Укажите срок исполнения"
                                    values={DueTimeModeTypeOption.filter(
                                        (x) => x.value === DueTimeModeType[periodType],
                                    )}
                                    multiselect={false}
                                    options={DueTimeModeTypeOption}
                                    preloader={loading}
                                    readOnly
                                    onChange={onPeriodTypeChange}
                                />
                            </FormControl>

                            {periodType == DueTimeModeType.TimeSpan && (
                                <>
                                    <InputNumberControl
                                        name="termCount"
                                        defaultValue={model?.defaultDueTime?.timeSpanValue ?? 1}
                                        min={1}
                                        max={9999}
                                        floatPoints={0}
                                        formState={formState.formState}
                                        control={formState.control}
                                    />

                                    <SelectControl
                                        multiselect={false}
                                        name="termType"
                                        readOnly
                                        options={DurationSpanTypeOption}
                                        /*defaultValue={[
                                            DurationSpanTypeOption.find(
                                                (option) => option.value === model?.defaultDueTime?.timeSpanType,
                                            ) ?? DurationSpanTypeOption[2],
                                        ]}*/
                                        preloader={loading}
                                        formState={formState.formState}
                                        control={formState.control}
                                    />
                                </>
                            )}

                            {periodType == DueTimeModeType.Date && (
                                <DatepickerControl
                                    name="periodDate"
                                    formState={formState.formState}
                                    control={formState.control}
                                />
                            )}

                            <TextareaControl
                                label="Текст задачи"
                                placeholder={'Текст задачи'}
                                readOnly={false}
                                required={true}
                                rules={{ required: 'Обязательное поле' }}
                                name="taskText"
                                defaultValue={model?.prefilledComment}
                                formState={formState.formState}
                                control={formState.control}
                                onValueChange={(val: any) => {}}
                            />
                        </HookFormProvider>
                    </FormProviderControl>
                </div>
            )}
        </Modal>
    );
};
